import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Collapse,
  Checkbox,
  FormControlLabel,
  Switch,
  Typography,
  Divider,
  TextField,
  MenuItem,
  Paper,
} from '@mui/material';

/**
 * MatterFactsForm — opt-in toggle for the post-execution To-Do List and
 * collector for the matter facts that drive its conditional sections.
 *
 * The toggle is visible for every package. When the user enables it, the
 * panel reveals checkboxes for facts about the client's assets (and, for
 * married packages, the spouse's assets + their plan details).
 *
 * Controlled component: the parent owns all state. On every change this
 * component invokes `onChange({ generateTodoList, matterFacts,
 * spouseMatterFacts, spousePlanDetails })` with the full current value so
 * the parent can pass the bundle straight into `buildGenerationPayload`.
 */

const CLIENT_GROUPS = [
  {
    heading: 'Insurance & retirement',
    items: [
      { key: 'has_life_insurance', label: 'Client has life insurance policies' },
      { key: 'has_ilit', label: 'Policies are held in an ILIT (Irrevocable Life Insurance Trust)' },
      { key: 'has_retirement_accounts', label: 'Client has retirement accounts (IRA, 401(k), etc.)' },
      { key: 'has_pension_plans', label: 'Client has a pension plan' },
    ],
  },
  {
    heading: 'Titles & accounts',
    items: [
      { key: 'has_joint_accounts', label: 'Couple has joint bank or investment accounts', marriedOnly: true },
      { key: 'has_closely_held_business', label: 'Client owns closely-held business interests' },
      { key: 'has_real_estate_to_retitle', label: 'Real property needs to be re-titled to the trust' },
    ],
  },
  {
    heading: 'Optional enclosures',
    items: [
      { key: 'has_memoranda_tangible_property', label: 'Include Memorandum for Disposition of Tangible Personal Property' },
      { key: 'has_digital_assets_inventory', label: 'Include Digital Assets & Online Account Inventory' },
      { key: 'has_letter_of_wishes', label: 'Include Letter of Wishes to Trustee' },
    ],
  },
];

// Spouse-scoped facts (subset of the client-side list — only the ones that
// change per spouse, not matter-wide facts like has_joint_accounts or
// optional enclosures).
const SPOUSE_ITEMS = [
  { key: 'has_life_insurance', label: 'Spouse has life insurance policies' },
  { key: 'has_ilit', label: 'Spouse\u2019s policies held in their own ILIT' },
  { key: 'has_retirement_accounts', label: 'Spouse has retirement accounts' },
  { key: 'has_pension_plans', label: 'Spouse has a pension plan' },
];

const PACKAGE_OPTIONS = [
  'Disclaimer Package',
  'Fractional QTIP Package',
  'Probate Avoidance Package',
];

const DEFAULT_MATTER_FACTS = {
  has_life_insurance: false,
  has_ilit: false,
  has_retirement_accounts: false,
  has_pension_plans: false,
  has_joint_accounts: false,
  has_closely_held_business: false,
  has_real_estate_to_retitle: false,
  has_memoranda_tangible_property: false,
  has_digital_assets_inventory: false,
  has_letter_of_wishes: false,
};

export default function MatterFactsForm({
  packageType,
  hasSpouse,
  initialMatterFacts = {},
  initialSpouseMatterFacts = {},
  initialSpousePlanDetails = {},
  initialGenerateTodoList = false,
  onChange,
}) {
  const isMarried = Boolean(hasSpouse);

  const [generateTodoList, setGenerateTodoList] = useState(initialGenerateTodoList);
  const [matterFacts, setMatterFacts] = useState({
    ...DEFAULT_MATTER_FACTS,
    ...initialMatterFacts,
  });
  const [spouseMatterFacts, setSpouseMatterFacts] = useState({
    ...DEFAULT_MATTER_FACTS,
    ...initialSpouseMatterFacts,
  });
  const [spousePlanDetails, setSpousePlanDetails] = useState({
    spouse_package_type: initialSpousePlanDetails.spouse_package_type || (isMarried ? (packageType || '') : ''),
    spouse_rlt_date: initialSpousePlanDetails.spouse_rlt_date || '',
  });

  // Notify the parent on every change with a fully-assembled payload
  // bundle. `spouseMatterFacts` / `spousePlanDetails` are passed as null
  // when the package is single-grantor so the parent doesn't accidentally
  // forward them.
  const bundle = useMemo(() => ({
    generateTodoList,
    matterFacts: generateTodoList ? matterFacts : null,
    spouseMatterFacts: generateTodoList && isMarried ? spouseMatterFacts : null,
    spousePlanDetails: generateTodoList && isMarried ? spousePlanDetails : null,
  }), [generateTodoList, matterFacts, spouseMatterFacts, spousePlanDetails, isMarried]);

  useEffect(() => {
    if (onChange) onChange(bundle);
  }, [bundle, onChange]);

  const setClientFact = useCallback((key, value) => {
    setMatterFacts((prev) => ({ ...prev, [key]: value }));
  }, []);

  const setSpouseFact = useCallback((key, value) => {
    setSpouseMatterFacts((prev) => ({ ...prev, [key]: value }));
  }, []);

  const renderCheckbox = (item, value, onToggle) => {
    if (item.marriedOnly && !isMarried) return null;
    return (
      <FormControlLabel
        key={item.key}
        control={
          <Checkbox
            checked={!!value}
            onChange={(e) => onToggle(item.key, e.target.checked)}
            size="small"
          />
        }
        label={<Typography variant="body2">{item.label}</Typography>}
        sx={{ display: 'block', ml: 0, mr: 0 }}
      />
    );
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <FormControlLabel
        control={
          <Switch
            checked={generateTodoList}
            onChange={(e) => setGenerateTodoList(e.target.checked)}
            color="primary"
          />
        }
        label={
          <Box>
            <Typography variant="body1" fontWeight={500}>
              Also generate the post-execution To-Do List
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Household-scoped document with beneficiary-designation language, title-update guidance, and banking instructions.
            </Typography>
          </Box>
        }
      />

      <Collapse in={generateTodoList} timeout="auto" unmountOnExit>
        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Client&rsquo;s matter facts
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Check everything that applies. Unchecked sections are suppressed in the rendered document.
        </Typography>

        {CLIENT_GROUPS.map((group) => {
          const visibleItems = group.items.filter((it) => !(it.marriedOnly && !isMarried));
          if (visibleItems.length === 0) return null;
          return (
            <Box key={group.heading} sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
                {group.heading}
              </Typography>
              {visibleItems.map((item) =>
                renderCheckbox(item, matterFacts[item.key], setClientFact)
              )}
            </Box>
          );
        })}

        {isMarried && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Spouse&rsquo;s matter facts
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Tell the template what applies to the spouse&rsquo;s own assets. Leave blank to mirror the client.
            </Typography>
            {SPOUSE_ITEMS.map((item) =>
              renderCheckbox(item, spouseMatterFacts[item.key], setSpouseFact)
            )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Spouse&rsquo;s plan
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Only needed if the spouses have different plan types or trust dates.
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              label="Spouse's package type"
              value={spousePlanDetails.spouse_package_type || ''}
              onChange={(e) =>
                setSpousePlanDetails((prev) => ({ ...prev, spouse_package_type: e.target.value }))
              }
              sx={{ mb: 2 }}
            >
              <MenuItem value="">Same as client&rsquo;s</MenuItem>
              {PACKAGE_OPTIONS.map((pkg) => (
                <MenuItem key={pkg} value={pkg}>{pkg}</MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              size="small"
              label="Spouse's trust date (e.g., May 15, 2026)"
              value={spousePlanDetails.spouse_rlt_date || ''}
              onChange={(e) =>
                setSpousePlanDetails((prev) => ({ ...prev, spouse_rlt_date: e.target.value }))
              }
              helperText="Leave blank to use the client's signing date"
            />
          </>
        )}
      </Collapse>
    </Paper>
  );
}
